import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { LeagueMatch } from '../../api/predictions.ts';
import { ApiErrorState } from '../../components/ApiErrorState.tsx';
import { SCORE_MAX, SCORE_MIN } from '../../config.ts';
import { formatDateTime, machineDateTime } from '../../lib/datetime.ts';
import { LeagueMatchPredictions } from './LeagueMatchPredictions.tsx';
import { fechaEsFiable, puedeEditar, puedeVerAjenos, tieneResultado } from './estado.ts';
import { useSubmitPrediction } from './hooks.ts';

interface Props {
  leagueId: string;
  match: LeagueMatch;
}

/** Un marcador vacío se distingue de un 0 escrito a propósito. */
function comoTexto(valor: number | null): string {
  return valor === null ? '' : String(valor);
}

export function MatchCard({ leagueId, match }: Props) {
  const { t, i18n } = useTranslation();
  const enviar = useSubmitPrediction(leagueId);

  const [local, setLocal] = useState(comoTexto(match.predicted_home_score));
  const [visitante, setVisitante] = useState(comoTexto(match.predicted_away_score));
  const [errorCampo, setErrorCampo] = useState<string | null>(null);
  const [guardado, setGuardado] = useState(false);

  // Si el servidor devuelve otro pronóstico (otra pestaña, o un envío que se cruzó),
  // los campos siguen a la verdad del servidor mientras no se estén editando.
  useEffect(() => {
    setLocal(comoTexto(match.predicted_home_score));
    setVisitante(comoTexto(match.predicted_away_score));
  }, [match.predicted_home_score, match.predicted_away_score]);

  const anulado = match.status === 'cancelled';
  const aplazado = match.status === 'postponed';
  // Quién decide el plazo está en `estado.ts`, con sus tests: LO DICE EL SERVIDOR.
  const editable = puedeEditar(match);
  const hayResultado = tieneResultado(match);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setGuardado(false);

    if (local.trim() === '' || visitante.trim() === '') {
      setErrorCampo('validation.scoreRequired');
      return;
    }
    const h = Number(local);
    const a = Number(visitante);
    if (!Number.isInteger(h) || !Number.isInteger(a)) {
      setErrorCampo('validation.scoreInteger');
      return;
    }
    if (h < SCORE_MIN || h > SCORE_MAX || a < SCORE_MIN || a > SCORE_MAX) {
      setErrorCampo('validation.scoreRange');
      return;
    }
    setErrorCampo(null);

    try {
      await enviar.mutateAsync({ matchId: match.match_id, home: h, away: a });
      setGuardado(true);
    } catch {
      // El error lo pinta <ApiErrorState> leyendo `enviar.error`. Se captura para que
      // un 409 no quede como promesa rechazada sin manejar, y sobre todo para que el
      // botón salga de "Enregistrement…": ese es el estado colgado que hay que evitar.
    }
  }

  return (
    <article
      className="tarjeta"
      data-test="partido"
      data-match-id={match.match_id}
      data-open={String(match.is_open)}
      data-visible={String(match.predictions_visible)}
      data-status={match.status}
      data-counts={String(match.counts_for_league)}
    >
      <header className="mb-3 flex items-baseline justify-between gap-3">
        {/* R9: de un aplazado NO se pinta la hora. La guardada es la vieja y ya se sabe
            falsa; enseñarla con formato de fecha exacta le da una credibilidad que no
            tiene, y alguien se organizaría el sábado por ella. */}
        {fechaEsFiable(match) ? (
          <time
            className="text-xs text-tenue"
            dateTime={machineDateTime(match.kickoff_time)}
            data-test="kickoff"
          >
            {formatDateTime(match.kickoff_time, i18n.language)}
          </time>
        ) : (
          <span className="text-xs text-aviso" data-test="kickoff-aplazado">
            {t('predictions.postponedDate')}
          </span>
        )}
        {match.is_knockout ? (
          <span className="rounded-full border border-aviso px-2 py-0.5 text-xs text-aviso">
            ×2
          </span>
        ) : null}
      </header>

      <p className="mb-3 font-semibold" data-test="equipos">
        {match.home_team} <span className="text-tenue">—</span> {match.away_team}
      </p>

      {hayResultado ? (
        <p className="mb-3 text-sm" data-test="resultado">
          <span className="text-tenue">{t('predictions.result')}: </span>
          <span className="font-mono text-lg font-bold tabular-nums">
            {match.actual_home_score} – {match.actual_away_score}
          </span>
        </p>
      ) : null}

      {anulado ? (
        <p className="aviso-atencion mb-3" data-test="anulado">
          {t('predictions.cancelled')}
        </p>
      ) : null}

      {aplazado ? (
        <p className="aviso-atencion mb-3" data-test="aplazado">
          {t('predictions.postponed')}
        </p>
      ) : null}

      {!match.counts_for_league ? (
        <p className="aviso-info mb-3" data-test="no-cuenta">
          {t('predictions.beforeYouJoined')}
        </p>
      ) : null}

      <form onSubmit={handleSubmit} noValidate data-test="formulario-pronostico">
        <fieldset disabled={!editable} className="m-0 border-0 p-0">
          <legend className="mb-2 text-sm font-semibold">
            {t('predictions.yourPrediction')}
          </legend>
          <div className="flex items-center gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-xs text-tenue">{match.home_team}</span>
              <input
                type="number"
                name="home_score"
                className="campo text-center"
                inputMode="numeric"
                min={SCORE_MIN}
                max={SCORE_MAX}
                step={1}
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                aria-invalid={errorCampo ? 'true' : undefined}
                aria-label={t('predictions.homeScore')}
              />
            </label>
            <span className="pt-5 text-tenue">–</span>
            <label className="flex-1">
              <span className="mb-1 block text-xs text-tenue">{match.away_team}</span>
              <input
                type="number"
                name="away_score"
                className="campo text-center"
                inputMode="numeric"
                min={SCORE_MIN}
                max={SCORE_MAX}
                step={1}
                value={visitante}
                onChange={(e) => setVisitante(e.target.value)}
                aria-invalid={errorCampo ? 'true' : undefined}
                aria-label={t('predictions.awayScore')}
              />
            </label>
          </div>

          {errorCampo ? (
            <p className="campo-error mt-2 text-sm text-alerta">{t(errorCampo)}</p>
          ) : null}

          {editable ? (
            <button
              className="btn-primario mt-3"
              type="submit"
              disabled={enviar.isPending}
              data-test="guardar-pronostico"
            >
              {enviar.isPending
                ? t('predictions.pending')
                : match.predicted_home_score !== null
                  ? t('predictions.update')
                  : t('predictions.save')}
            </button>
          ) : null}
        </fieldset>
      </form>

      {/* El 409 de "fuera de plazo" acaba aquí, con su mensaje y sin dejar el botón
          girando: la mutación termina siempre y la lista se vuelve a pedir. */}
      <div className="mt-3">
        <ApiErrorState error={enviar.error} />
      </div>

      {guardado && !enviar.isError ? (
        <p className="aviso-info mt-3" role="status" data-test="guardado">
          {t('predictions.saved')}
        </p>
      ) : null}

      {/* Un aplazado también está cerrado, pero NO porque el partido haya empezado:
          decírselo así al usuario sería contradecir el aviso de arriba. */}
      {!editable && !anulado && !aplazado ? (
        <p className="mt-3 text-xs text-tenue" data-test="cerrado">
          {match.predicted_home_score === null
            ? t('predictions.notPredicted')
            : t('predictions.closedHint')}
        </p>
      ) : null}

      {aplazado ? (
        <p className="mt-3 text-xs text-tenue" data-test="cerrado-aplazado">
          {match.predicted_home_score === null
            ? t('predictions.notPredicted')
            : t('predictions.postponedKept')}
        </p>
      ) : null}

      {/* R15/visibilidad social: los pronósticos ajenos, solo a partir del kickoff. */}
      {puedeVerAjenos(match) ? (
        <LeagueMatchPredictions leagueId={leagueId} matchId={match.match_id} />
      ) : (
        <p className="mt-3 text-xs text-tenue" data-test="ajenos-ocultos">
          {t('predictions.others.hiddenUntilKickoff')}
        </p>
      )}
    </article>
  );
}
